/**
 * @copyright Copyright 2015-2017 MathWorks, Inc.
 */
define([
    'dojo/_base/declare',
    'dojo/_base/Color'
], function (declare, Color) {
    /**
     * MColor is used to create a object that adds MATLAB color conversion APIs to dojo/_base/Color
     *
     * @exports mw-colorpicker/MColor
     * @mixes  dojo/_base/Color
     *
     * @example <caption>Simple Color Object</caption>
     * var mc = new MColor("#F0F0F0");
     * mc.toDoubleRGBString() // returns string "[0.94 0.94 0.94]"
     * mc.toDoubleRGB() // returns Array [.94, .94, .94]
     *
     * var redColor = new MColor("#FF0000");
     * redColor.toMATLABFriendlyName()
     * // returns string with double RGB + the MATLAB ColorSpec: "[1.00 0.00 0.00] - red (r)"
     */
    var _mcolor = declare([Color], {

        /**
         * toDoubleRGBString converts the Color to a double RGB string with
         * numbers rounded to 2 decimal places.
         *
         * @instance
         *
         * @return {String} returns a double RGB string
         */
        toDoubleRGBString: function () {
            var d = this.toDoubleRGB();
            return '[' + d[0].toFixed(2) + ' ' + d[1].toFixed(2) + ' ' + d[2].toFixed(2) + ']';
        },

        /**
         * toMATLABFriendlyName returns the Color as a string matching the name
         * of the MATLAB Colorspec. MATLAB Color spec colors are:<br/>
         * Red (r), Magenta (M), Yellow (y), Green (g), Cyan (c), Blue (b), Black (k), White (w)
         *
         * @instance
         *
         */
        toMATLABFriendlyName: function () {
            var colorSpecName = _mcolor.namedMATLABColors[this.toHex().toLowerCase()];
            return (colorSpecName) ? this.toDoubleRGBString() + ' - ' + colorSpecName
                : this.toDoubleRGBString();
        },

        /**
         * toDoubleRGB Returns array of RGB values as a double ranging between 0..1
         *
         * @instance
         *
         * @return {double[]} Returns 3 component array of RGB values
         */
        toDoubleRGB: function () {
            return [(this.r / 255), (this.g / 255), (this.b / 255)];
        }

    });

    /**
     * fromDoubleRGB Returns a new MColor instance created using doubles ranging between 0..1
     * @param {double[]} colorArray 1x3 double array representing a color.
     * @static
     *
     * @return {MColor} Returns MColor instance
     */
    _mcolor.fromDoubleRGB = function (c) {
        var cInt = c.map(function (i) {
            return Math.round(255 * i);
        });
        return new _mcolor(cInt);
    };

    /**
     * toHSV Returns an array of [H, S, V] values
     * @param {double[]} colorArray 1x3 double array representing a color.
     * @static
     *
     * @return {double[]} Returns 3 component array of HSV values
     */
    _mcolor.toHSV = function (c) {
        var red = c[0];
        var green = c[1];
        var blue = c[2];
        var rr, gg, bb;
        var r = red / 255;
        var g = green / 255;
        var b = blue / 255;
        var h, s;
        var v = Math.max(r, g, b);
        var diff = v - Math.min(r, g, b);
        var diffc = function (c) {
            return (v - c) / 6 / diff + 1 / 2;
        };

        if (diff === 0) {
            h = 0;
            s = 0;
        } else {
            s = diff / v;
            rr = diffc(r);
            gg = diffc(g);
            bb = diffc(b);

            if (r === v) {
                h = bb - gg;
            } else if (g === v) {
                h = (1 / 3) + rr - bb;
            } else if (b === v) {
                h = (2 / 3) + gg - rr;
            }

            if (h < 0) {
                h += 1;
            } else if (h > 1) {
                h -= 1;
            }
        }
        return [Math.round(h * 360),
            Math.round(s * 100),
            Math.round(v * 100)];
    };

    /**
     * fromHSV Returns a new MColor instance created using an HSV value
     * @param {double[]} colorArray 1x3 representating the H, S and V values respectively
     * @static
     *
     * @return {MColor} Returns MColor instance
     */
    _mcolor.fromHSV = function (c) {
        var hue = c[0];
        var saturation = c[1] / 100;
        var value = c[2] / 100;
        if (hue === 360) {
            hue = 0;
        }
        var r, g, b;
        if (saturation === 0) {
            r = value;
            b = value;
            g = value;
        } else {
            var hTemp = hue / 60;
            var i = Math.floor(hTemp);
            var f = hTemp - i;
            var p = value * (1 - saturation);
            var q = value * (1 - (saturation * f));
            var t = value * (1 - (saturation * (1 - f)));
            switch (i) {
                case 0:
                {
                    r = value;
                    g = t;
                    b = p;
                    break;
                }
                case 1:
                {
                    r = q;
                    g = value;
                    b = p;
                    break;
                }
                case 2:
                {
                    r = p;
                    g = value;
                    b = t;
                    break;
                }
                case 3:
                {
                    r = p;
                    g = q;
                    b = value;
                    break;
                }
                case 4:
                {
                    r = t;
                    g = p;
                    b = value;
                    break;
                }
                case 5:
                {
                    r = value;
                    g = p;
                    b = q;
                    break;
                }
            }
        }

        var newR = Math.round(r * 255);
        var newG = Math.round(g * 255);
        var newB = Math.round(b * 255);
        return new _mcolor([newR, newG, newB]);
    };

    /**
     * getStandardColors Returns 4x7 array of hex colors of RGB values<br/>
     *
     * <ul>
     * <li>Row1: MATLAB Default Axes ColorOrder</li>
     * <li>Row2: Color Order for dark backgrounds like Scopes</li>
     * <li>Row3: Standard gray scale colors:<br/>
     *           white (w), fig bk (6%), 10%, HG1 fig bk (20%), 35%, 50%, plot black (85%)</li>
     * <li>Row4: MATLAB Color spec:<br/>
     *           Red (r), Magenta (M), Yellow (y), Green (g), Cyan (c), Blue (b), Black (k)</li>
     * </ul>
     * @static
     * @return {string[]} Returns 4x7 array of hex string values<br/>
     */

    _mcolor.getStandardColors = function () {
        var lightColors = [
            // MATLAB Default Axes ColorOrder
            ['#0072bd', '#d95319', '#edb120', '#7e2f8e', '#77ac30', '#4dbeee', '#a2142f'],

            // Color Order for dark backgrounds like Scopes
            ['#ffff11', '#139fff', '#ff6929', '#64d413', '#b746ff', '#0fffff', '#ff13a6'],

            // Standard gray scale colors:
            // white (w), fig bk (6%), 10%, HG1 fig bk (20%), 35%, 50%, plot black (85%)
            ['#ffffff', '#f0f0f0', '#e6e6e6', '#cccccc', '#a6a6a6', '#808080', '#262626'],

            // MATLAB Color spec:
            // Red (r), Magenta (M), Yellow (y), Green (g), Cyan (c), Blue (b), Black (k)
            ['#ff0000', '#ff00ff', '#ffff00', '#00ff00', '#00ffff', '#0000ff', '#000000']
        ];
        // Return a copy of the array
        return lightColors.slice();
    };

    /**
         * fromMATLABFriendlyName returns the hex value that matches the given name
         * of the MATLAB Colorspec. MATLAB Color spec colors are:<br/>
         * Red, Magenta, Yellow, Green, Cyan, Blue, Black, White
         *
         * @instance
         *
         */
    _mcolor.fromMATLABFriendlyName = function (name) {
        return _mcolor.namedMATLABColorStrings[name.toLowerCase()];
    };

    _mcolor.namedMATLABColors = {
        '#ff0000': 'red (r)',
        '#ff00ff': 'magenta (m)',
        '#ffff00': 'yellow (y)',
        '#00ff00': 'green (g)',
        '#00ffff': 'cyan (c)',
        '#0000ff': 'blue (b)',
        '#000000': 'black (k)',
        '#ffffff': 'white (w)'
    };

    _mcolor.namedMATLABColorStrings = {
        'red': '#ff0000',
        'magenta': '#ff00ff',
        'yellow': '#ffff00',
        'green': '#00ff00',
        'cyan': '#00ffff',
        'blue': '#0000ff',
        'black': '#000000',
        'white': '#ffffff'
    };

    return _mcolor;
});
